import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { 
  FileText, Calendar, Users, Euro, ChevronRight, ChevronLeft,
  CheckCircle2, AlertCircle, Loader2, Download, Eye, Save,
  Building2, User, Calculator, Gavel, Sparkles, AlertTriangle, FileUp, Mail, X
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Templates de résolutions supplémentaires pour AGO
const AGO_SUPP_TEMPLATES = [
  {
    label: "Ratification d'actes du dirigeant",
    description: "Ratification des actes accomplis avant nomination ou hors mandat",
    titre: "RÉSOLUTION SUPPLÉMENTAIRE — Ratification d'actes",
    contenu:
      "L'Assemblée Générale, après avoir pris connaissance des actes accomplis par [Nom Prénom, fonction] pour le compte de la société [avant sa nomination / hors du champ de son mandat] au cours de l'exercice clos le [date], ratifie expressément ces actes et en assume l'entière responsabilité. Ces actes concernent notamment : [détail des actes ratifiés — contrats signés, décisions prises, etc.]. En conséquence, la société assume l'ensemble des obligations qui en découlent."
  },
  {
    label: "Quitus au(x) dirigeant(s)",
    description: "Décharge de gestion pour l'exercice écoulé",
    titre: "RÉSOLUTION SUPPLÉMENTAIRE — Quitus de gestion",
    contenu:
      "L'Assemblée Générale, après avoir pris connaissance des comptes et du rapport de gestion, donne quitus entier et sans réserve à Monsieur/Madame [Nom Prénom], [Gérant/Président/Directeur Général] de la société, de l'exécution de son mandat pour l'exercice clos le [date de clôture]. Elle le décharge de toute responsabilité au titre de sa gestion pour cet exercice."
  },
  {
    label: "Approbation conventions réglementées",
    description: "Art. L.223-19 (SARL) / L.225-40 (SA) / L.227-10 (SAS)",
    titre: "RÉSOLUTION SUPPLÉMENTAIRE — Conventions réglementées",
    contenu:
      "L'Assemblée Générale, après avoir pris connaissance du rapport spécial du [Gérant/Commissaire aux comptes] sur les conventions visées à l'article [L.223-19 pour SARL / L.225-38 pour SA / L.227-10 pour SAS] du Code de commerce, approuve les conventions qui y sont mentionnées, conclues au cours de l'exercice clos le [date]. Ces conventions sont approuvées dans les termes du rapport spécial. [Le cas échéant : les intéressés n'ont pas pris part au vote.]"
  },
  {
    label: "Renouvellement mandat CAC",
    description: "Renouvellement du Commissaire aux comptes titulaire",
    titre: "RÉSOLUTION SUPPLÉMENTAIRE — Renouvellement du mandat du Commissaire aux comptes",
    contenu:
      "L'Assemblée Générale, constatant que le mandat du Commissaire aux comptes titulaire, [Cabinet / Nom Prénom], arrive à expiration à l'issue de la présente assemblée, décide de renouveler son mandat pour une durée de SIX (6) exercices, soit jusqu'à l'issue de l'assemblée générale ordinaire appelée à statuer sur les comptes de l'exercice clos le [date de fin de mandat]. [Le cas échéant : le Commissaire aux comptes suppléant est également renouvelé dans les mêmes conditions.]"
  },
  {
    label: "Fixation rémunération dirigeant",
    description: "Fixation ou modification de la rémunération du gérant/président",
    titre: "RÉSOLUTION SUPPLÉMENTAIRE — Rémunération du dirigeant",
    contenu:
      "L'Assemblée Générale fixe la rémunération de Monsieur/Madame [Nom Prénom], [Gérant/Président] de la société, à la somme brute annuelle de [montant] euros pour l'exercice en cours, versée mensuellement à raison de [montant mensuel] euros bruts. Cette rémunération est indépendante des remboursements de frais professionnels sur justificatifs. [Le cas échéant : elle inclut / n'inclut pas les avantages en nature suivants : [détail].]"
  },
  {
    label: "Pouvoirs pour formalités",
    description: "Clause finale classique — délégation formalités RCS/BODACC",
    titre: "RÉSOLUTION SUPPLÉMENTAIRE — Pouvoirs pour formalités",
    contenu:
      "Tous pouvoirs sont donnés au porteur d'une copie ou d'un extrait certifié conforme du présent procès-verbal pour effectuer toutes formalités légales de publicité, de dépôt et d'inscription auprès du Registre du commerce et des sociétés, du BODACC, du journal d'annonces légales, ainsi que toute autre formalité utile."
  }
];

const WIZARD_STEPS = [
  { id: "exercice", title: "Exercice", icon: Calendar },
  { id: "date", title: "Date & Heure", icon: Calendar },
  { id: "participants", title: "Participants", icon: Users },
  { id: "resultats", title: "Résultats", icon: Euro },
  { id: "affectation", title: "Affectation", icon: Calculator },
  { id: "preview", title: "Aperçu", icon: Eye }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

export default function GenerateurAGO() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    exercice: new Date().getFullYear() - 1,
    date_ago: "",
    heure_ago: "10h00",
    heure_fin: "11h00",
    date_convocation: "",
    resultat_net: 0,
    chiffre_affaires: 0,
    affectation: {
      reserve_legale: 0,
      dividendes: 0,
      report_nouveau: 0
    },
    secretaire_id: null,
    secretaire_externe: "", // Nom du secrétaire externe (saisie manuelle)
    presences: {},
    conventions_reglementees: [],
    resolutions_supplementaires: []
  });
  
  // Preview PDF URL
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Convocation generation state
  const [generatingConvocations, setGeneratingConvocations] = useState(false);
  const [generatedConvocations, setGeneratedConvocations] = useState([]);
  const [convocationError, setConvocationError] = useState(null);
  const [showResolutionsSupp, setShowResolutionsSupp] = useState(false);

  // Initialize wizard
  const initWizard = useCallback(async (exercice) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/corporate/ago/wizard/init`, {
        params: { exercice },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWizardData(res.data);
      
      // Pre-fill form with existing draft or defaults
      const draft = res.data.existing_draft?.data;
      const defaults = res.data.default_values;
      const financials = res.data.financials;
      const suggested = res.data.suggested_affectation;
      
      // Use resultat_net from bilan if available, otherwise resultat_brut
      const resultatNet = financials.resultat_net || financials.resultat_brut || 0;
      
      setFormData(prev => ({
        ...prev,
        exercice: res.data.exercice,
        date_ago: draft?.date_ago || defaults.date_ago,
        heure_ago: draft?.heure_ago || defaults.heure_ago,
        heure_fin: draft?.heure_fin || defaults.heure_fin,
        date_convocation: draft?.date_convocation || defaults.date_convocation,
        resultat_net: draft?.resultat_net || resultatNet,
        chiffre_affaires: draft?.chiffre_affaires || financials.chiffre_affaires || 0,
        affectation: draft?.affectation || suggested || prev.affectation,
        presences: draft?.presences || {}
      }));
      
    } catch (error) {
      console.error("Error initializing wizard:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'initialisation");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    initWizard(formData.exercice);
  }, []);

  // Update field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Auto-format date input (adds / automatically)
  const handleDateChange = (field, value) => {
    // Remove any non-digit characters except /
    let cleaned = value.replace(/[^\d/]/g, '');
    
    // Remove existing slashes to reformat
    const digits = cleaned.replace(/\//g, '');
    
    // Format as JJ/MM/AAAA
    let formatted = '';
    for (let i = 0; i < digits.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += digits[i];
    }
    
    // If date_ago is being updated and we have a complete date, auto-calculate convocation date
    if (field === "date_ago" && formatted.length === 10) {
      const parts = formatted.split('/');
      if (parts.length === 3) {
        const agoDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        if (!isNaN(agoDate.getTime())) {
          // Convocation must be at least 20 days before AGO
          const convocationDate = new Date(agoDate);
          convocationDate.setDate(convocationDate.getDate() - 20);
          const convFormatted = `${String(convocationDate.getDate()).padStart(2, '0')}/${String(convocationDate.getMonth() + 1).padStart(2, '0')}/${convocationDate.getFullYear()}`;
          
          setFormData(prev => ({
            ...prev,
            [field]: formatted,
            date_convocation: convFormatted
          }));
          setConvocationError(null);
          return;
        }
      }
    }
    
    // If date_convocation is being updated, validate it's at least 20 days before AGO
    if (field === "date_convocation" && formatted.length === 10 && formData.date_ago?.length === 10) {
      const convParts = formatted.split('/');
      const agoParts = formData.date_ago.split('/');
      
      if (convParts.length === 3 && agoParts.length === 3) {
        const convDate = new Date(parseInt(convParts[2]), parseInt(convParts[1]) - 1, parseInt(convParts[0]));
        const agoDate = new Date(parseInt(agoParts[2]), parseInt(agoParts[1]) - 1, parseInt(agoParts[0]));
        
        if (!isNaN(convDate.getTime()) && !isNaN(agoDate.getTime())) {
          const diffDays = Math.floor((agoDate - convDate) / (1000 * 60 * 60 * 24));
          
          if (diffDays < 20) {
            setConvocationError(`La date de convocation doit être au minimum 20 jours avant l'AGO (actuellement ${diffDays} jours)`);
          } else {
            setConvocationError(null);
          }
        }
      }
    }
    
    updateField(field, formatted);
  };

  // Update affectation - allow empty values for better UX
  // Auto-calculate report_nouveau when reserve_legale or dividendes change
  const updateAffectation = (field, value) => {
    // Allow empty string to clear the field
    const numValue = value === "" ? "" : (parseFloat(value) || 0);
    
    setFormData(prev => {
      const newAffectation = { ...prev.affectation, [field]: numValue };
      
      // Auto-calculate report_nouveau when other fields change
      if (field === "reserve_legale" || field === "dividendes") {
        const reserveLegale = field === "reserve_legale" ? (numValue || 0) : (parseFloat(prev.affectation.reserve_legale) || 0);
        const dividendes = field === "dividendes" ? (numValue || 0) : (parseFloat(prev.affectation.dividendes) || 0);
        newAffectation.report_nouveau = prev.resultat_net - reserveLegale - dividendes;
      }
      
      return {
        ...prev,
        affectation: newAffectation
      };
    });
  };

  // Get display value for affectation inputs (show empty string instead of 0)
  const getAffectationValue = (value) => {
    if (value === "" || value === 0) return "";
    return value;
  };

  // Update presence
  const updatePresence = (associeId, presence) => {
    setFormData(prev => ({
      ...prev,
      presences: { ...prev.presences, [associeId]: presence }
    }));
  };

  // Save draft
  const saveDraft = async () => {
    try {
      await axios.post(`${API}/api/corporate/ago/draft`, {
        exercice: formData.exercice,
        data: formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Brouillon enregistré");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // Generate preview
  const generatePreview = async () => {
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      // Calculate report_nouveau for submission
      const submitData = {
        ...formData,
        affectation: {
          ...formData.affectation,
          report_nouveau: formData.resultat_net - 
                          getNumericValue(formData.affectation.reserve_legale) - 
                          getNumericValue(formData.affectation.dividendes)
        }
      };
      
      const res = await axios.post(`${API}/api/corporate/ago/generate/preview`, submitData, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        validateStatus: (status) => status < 500 // Accept 4xx errors to handle them
      });
      
      // Check if response is an error (4xx status)
      if (res.status >= 400) {
        // Try to read error message from blob
        const text = await res.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.detail || "Erreur lors de la génération");
        } catch (parseError) {
          throw new Error("Erreur lors de la génération de l'aperçu");
        }
      }
      
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      
    } catch (error) {
      console.error("Error generating preview:", error);
      const errorMsg = error.message || error.response?.data?.detail || "Erreur lors de la génération de l'aperçu";
      toast.error(errorMsg);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Generate final PDF
  const generateFinal = async () => {
    setGenerating(true);
    try {
      // Calculate report_nouveau for submission
      const submitData = {
        ...formData,
        affectation: {
          ...formData.affectation,
          report_nouveau: formData.resultat_net - 
                          getNumericValue(formData.affectation.reserve_legale) - 
                          getNumericValue(formData.affectation.dividendes)
        }
      };
      
      const res = await axios.post(`${API}/api/corporate/ago/generate/final`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("PV AGO généré et enregistré dans le Coffre-Fort !");
      
      // Redirect or show success
      setCurrentStep(0);
      initWizard(formData.exercice + 1); // Reset for next year
      
    } catch (error) {
      console.error("Error generating final:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  // Generate convocation letters for all associates
  const generateConvocations = async () => {
    if (!formData.date_ago || !formData.date_convocation) {
      toast.error("Veuillez d'abord renseigner les dates de l'AGO et de convocation");
      return;
    }
    
    setGeneratingConvocations(true);
    try {
      // Don't send lieu_ago if not defined - backend will use siege_social
      const requestData = {
        exercice: formData.exercice,
        date_ago: formData.date_ago,
        heure_ago: formData.heure_ago || "10h00",
        date_convocation: formData.date_convocation
      };
      
      // Only add lieu_ago if explicitly set
      if (formData.lieu_ago) {
        requestData.lieu_ago = formData.lieu_ago;
      }
      
      const res = await axios.post(`${API}/api/corporate/ago/convocations/generate`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGeneratedConvocations(res.data.convocations || []);
      toast.success(res.data.message || "Lettres de convocation générées !");
      
    } catch (error) {
      console.error("Error generating convocations:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la génération des convocations");
    } finally {
      setGeneratingConvocations(false);
    }
  };

  // Download a convocation PDF
  const downloadConvocation = async (convId, filename) => {
    try {
      const res = await axios.get(`${API}/api/corporate/ago/convocations/${convId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  // Navigation
  const nextStep = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
      
      // Generate preview when reaching last step
      if (currentStep === WIZARD_STEPS.length - 2) {
        generatePreview();
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Calculate totals (handle empty strings as 0)
  const getNumericValue = (val) => (val === "" || val === undefined || val === null) ? 0 : parseFloat(val) || 0;
  
  // Report à nouveau is auto-calculated
  const calculatedReportNouveau = formData.resultat_net - 
                                   getNumericValue(formData.affectation.reserve_legale) - 
                                   getNumericValue(formData.affectation.dividendes);
  
  const totalAffectation = getNumericValue(formData.affectation.reserve_legale) + 
                           getNumericValue(formData.affectation.dividendes) + 
                           calculatedReportNouveau;
  
  // With auto-calculation, validation always passes (as long as values are non-negative)
  const affectationValid = calculatedReportNouveau >= 0 || formData.resultat_net < 0;
  
  // Check if profile is configured
  const profileConfigured = wizardData?.company && (
    wizardData.company.raison_sociale || 
    wizardData.company.associes?.length > 0 || 
    wizardData.company.dirigeants?.length > 0
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  // Show error if profile not configured
  if (!profileConfigured) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Profil société non configuré</h3>
        <p className="text-gray-500 mb-4 max-w-md">
          Pour générer un PV d'AGO, vous devez d'abord configurer le profil de la société 
          (raison sociale, associés, dirigeants) via l'import de documents ou manuellement.
        </p>
        <Button onClick={() => window.location.href = '/droit-societes?tab=import'} className="gap-2">
          <FileUp className="w-4 h-4" />
          Importer des documents
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
            <Gavel className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Génération AGO</h2>
            <p className="text-sm text-gray-500">Exercice {formData.exercice}</p>
          </div>
        </div>
        
        <Button variant="outline" onClick={saveDraft} className="gap-2">
          <Save className="w-4 h-4" />
          Sauvegarder brouillon
        </Button>
      </div>

      {/* Progress Steps */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-2">
          {WIZARD_STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isActive = idx === currentStep;
            const isCompleted = idx < currentStep;
            
            return (
              <div 
                key={step.id}
                className={`flex items-center ${idx < WIZARD_STEPS.length - 1 ? 'flex-1' : ''}`}
              >
                <div 
                  className={`flex items-center gap-2 cursor-pointer ${
                    isActive ? 'text-indigo-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                  }`}
                  onClick={() => idx <= currentStep && setCurrentStep(idx)}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isActive ? 'bg-indigo-100' : isCompleted ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <span className="hidden md:block text-sm font-medium">{step.title}</span>
                </div>
                
                {idx < WIZARD_STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    isCompleted ? 'bg-green-300' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
        <Progress value={(currentStep / (WIZARD_STEPS.length - 1)) * 100} className="h-1" />
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="p-6">
          {/* Step 0: Exercice */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Choisir l'exercice</h3>
                <p className="text-gray-500">Sélectionnez l'exercice pour lequel générer l'AGO</p>
              </div>
              
              <div className="max-w-xs mx-auto">
                <Label htmlFor="exercice">Exercice fiscal</Label>
                <Select 
                  value={String(formData.exercice)} 
                  onValueChange={(v) => {
                    updateField("exercice", parseInt(v));
                    initWizard(parseInt(v));
                  }}
                >
                  <SelectTrigger data-testid="exercice-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - 1 - i;
                      const hasImportedBilan = wizardData?.available_exercices?.some(e => e.annee === year);
                      return (
                        <SelectItem key={year} value={String(year)}>
                          {year} {hasImportedBilan && "✓ Bilan importé"}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Data Source Indicator */}
              {wizardData?.financials && (
                <div className="max-w-2xl mx-auto">
                  {wizardData.financials.bilan_imported ? (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Données issues du bilan importé</p>
                        <p className="text-sm text-green-600">
                          Fichier: {wizardData.financials.filename}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">Données calculées depuis les factures</p>
                        <p className="text-sm text-amber-600">
                          Pour des données plus précises, importez le bilan {formData.exercice} via l'onglet Import
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {wizardData?.financials && (
                <Card className={`mt-6 ${wizardData.financials.bilan_imported 
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' 
                  : 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200'}`}>
                  <CardContent className="p-4">
                    <h4 className={`font-semibold mb-3 flex items-center gap-2 ${
                      wizardData.financials.bilan_imported ? 'text-green-900' : 'text-indigo-900'
                    }`}>
                      <Sparkles className="w-4 h-4" />
                      Données financières {formData.exercice}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Chiffre d'affaires</p>
                        <p className="font-bold text-green-600">{formatCurrency(wizardData.financials.chiffre_affaires)}</p>
                      </div>
                      {wizardData.financials.bilan_imported ? (
                        <>
                          <div>
                            <p className="text-gray-500">Résultat d'exploitation</p>
                            <p className="font-bold text-blue-600">{formatCurrency(wizardData.financials.resultat_exploitation)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Résultat net</p>
                            <p className={`font-bold ${wizardData.financials.resultat_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(wizardData.financials.resultat_net)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Capitaux propres</p>
                            <p className="font-bold text-indigo-600">{formatCurrency(wizardData.financials.capitaux_propres)}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <p className="text-gray-500">Charges</p>
                            <p className="font-bold text-red-600">{formatCurrency(wizardData.financials.charges)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Résultat brut</p>
                            <p className={`font-bold ${wizardData.financials.resultat_brut >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                              {formatCurrency(wizardData.financials.resultat_brut)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Effectif moyen</p>
                            <p className="font-bold text-gray-900">{wizardData.financials.effectif_moyen} salarié(s)</p>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Additional bilan info */}
                    {wizardData.financials.bilan_imported && (
                      <div className="mt-4 pt-4 border-t border-green-200">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Réserve légale actuelle</p>
                            <p className="font-medium">{formatCurrency(wizardData.financials.reserve_legale)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Report à nouveau</p>
                            <p className="font-medium">{formatCurrency(wizardData.financials.report_a_nouveau)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Autres réserves</p>
                            <p className="font-medium">{formatCurrency(wizardData.financials.autres_reserves)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Effectif moyen</p>
                            <p className="font-medium">{wizardData.financials.effectif_moyen} salarié(s)</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Available exercices info */}
              {wizardData?.available_exercices?.length > 0 && (
                <div className="max-w-2xl mx-auto">
                  <p className="text-sm text-gray-500 mb-2">Bilans importés disponibles :</p>
                  <div className="flex flex-wrap gap-2">
                    {wizardData.available_exercices.map(ex => (
                      <Badge 
                        key={ex.annee} 
                        variant={ex.annee === formData.exercice ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          updateField("exercice", ex.annee);
                          initWizard(ex.annee);
                        }}
                      >
                        {ex.annee} - {formatCurrency(ex.compte_resultat?.resultat_net || 0)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 1: Date & Heure */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Date et heure de l'AGO</h3>
                <p className="text-gray-500">Définissez quand se tiendra l'assemblée</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <div className="space-y-2">
                  <Label htmlFor="date_ago">Date de l'AGO *</Label>
                  <Input
                    id="date_ago"
                    type="text"
                    value={formData.date_ago}
                    onChange={(e) => handleDateChange("date_ago", e.target.value)}
                    placeholder="JJ/MM/AAAA"
                    maxLength={10}
                    data-testid="date-ago-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="date_convocation">Date de convocation</Label>
                  <Input
                    id="date_convocation"
                    type="text"
                    value={formData.date_convocation}
                    onChange={(e) => handleDateChange("date_convocation", e.target.value)}
                    placeholder="JJ/MM/AAAA"
                    maxLength={10}
                    className={convocationError ? "border-red-500" : ""}
                  />
                  {convocationError ? (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {convocationError}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">
                      Minimum 20 jours avant l'AGO (délai légal)
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="heure_ago">Heure de début</Label>
                  <Input
                    id="heure_ago"
                    type="text"
                    value={formData.heure_ago}
                    onChange={(e) => updateField("heure_ago", e.target.value)}
                    placeholder="10h00"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="heure_fin">Heure de fin</Label>
                  <Input
                    id="heure_fin"
                    type="text"
                    value={formData.heure_fin}
                    onChange={(e) => updateField("heure_fin", e.target.value)}
                    placeholder="11h00"
                  />
                </div>
              </div>
              
              {/* Convocation Letters Section */}
              <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-amber-800">Lettres de convocation</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Générez automatiquement les lettres de convocation pour chaque associé (délai légal: 20 jours avant l'AGO)
                    </p>
                    
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        onClick={generateConvocations}
                        disabled={generatingConvocations || !formData.date_ago || !formData.date_convocation}
                        className="gap-2 border-amber-300 hover:bg-amber-100"
                      >
                        {generatingConvocations ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                        Générer les convocations
                      </Button>
                    </div>
                    
                    {/* List of generated convocations */}
                    {generatedConvocations.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-amber-800">
                          {generatedConvocations.length} convocation(s) générée(s) :
                        </p>
                        {generatedConvocations.map((conv) => (
                          <div key={conv.id} className="flex items-center justify-between bg-white p-2 rounded border border-amber-200">
                            <span className="text-sm text-gray-700">{conv.associe_nom}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadConvocation(conv.id, conv.filename)}
                              className="gap-1 text-amber-700 hover:text-amber-800"
                            >
                              <Download className="w-4 h-4" />
                              PDF
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Participants */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Participants</h3>
                <p className="text-gray-500">Confirmez la présence des associés</p>
              </div>
              
              <div className="space-y-4">
                {wizardData?.associes?.map((associe) => (
                  <div 
                    key={associe.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        {associe.type === "personne_morale" ? (
                          <Building2 className="w-5 h-5 text-purple-600" />
                        ) : (
                          <User className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {associe.type === "personne_morale" 
                            ? associe.denomination 
                            : `${associe.civilite || ""} ${associe.prenom || ""} ${associe.nom || ""}`
                          }
                        </p>
                        <p className="text-sm text-gray-500">
                          {associe.nombre_parts} parts ({associe.pourcentage}%)
                        </p>
                      </div>
                    </div>
                    
                    <Select 
                      value={formData.presences[associe.id] || "Présent"}
                      onValueChange={(v) => updatePresence(associe.id, v)}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Présent">Présent</SelectItem>
                        <SelectItem value="Représenté">Représenté</SelectItem>
                        <SelectItem value="Absent">Absent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              
              {/* Secretaire selection */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                <Label className="text-blue-800">Secrétaire de séance (optionnel)</Label>
                <Select 
                  value={formData.secretaire_id || "none"}
                  onValueChange={(v) => {
                    updateField("secretaire_id", v === "none" ? null : v);
                    // Clear external name if selecting an associate
                    if (v !== "externe") {
                      updateField("secretaire_externe", "");
                    }
                  }}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Sélectionner un secrétaire" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {wizardData?.associes?.filter(a => a.type !== "personne_morale").map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.civilite} {a.prenom} {a.nom}
                      </SelectItem>
                    ))}
                    <SelectItem value="externe">Autre (saisie manuelle)</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Input for external secretary name */}
                {formData.secretaire_id === "externe" && (
                  <div className="pt-2">
                    <Label className="text-blue-700 text-sm">Nom du secrétaire</Label>
                    <Input
                      placeholder="Ex: M. Jean DUPONT"
                      value={formData.secretaire_externe || ""}
                      onChange={(e) => updateField("secretaire_externe", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Résultats */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Résultats financiers</h3>
                <p className="text-gray-500">Confirmez ou ajustez les données financières</p>
              </div>
              
              {/* Message proactif si données incomplètes */}
              {formData.chiffre_affaires === 0 && formData.resultat_net === 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3 max-w-2xl mx-auto">
                  <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-orange-800">Attention : Vos données {formData.exercice} sont incomplètes</p>
                    <p className="text-orange-700 mt-1">
                      Le chiffre d'affaires et le résultat net sont à 0€. Vous avez deux options :
                    </p>
                    <ul className="mt-2 space-y-1 text-orange-700">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                        <strong>Saisir manuellement</strong> les montants depuis votre liasse fiscale
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                        <strong>Importer votre bilan</strong> dans le Coffre-Fort pour auto-remplissage futur
                      </li>
                    </ul>
                    <p className="text-xs text-orange-600 mt-2 italic">
                      Vous pouvez continuer avec les valeurs actuelles si votre société n'a pas eu d'activité.
                    </p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <div className="space-y-2">
                  <Label htmlFor="chiffre_affaires">Chiffre d'affaires</Label>
                  <div className="relative">
                    <Input
                      id="chiffre_affaires"
                      type="number"
                      value={formData.chiffre_affaires}
                      onChange={(e) => updateField("chiffre_affaires", parseFloat(e.target.value) || 0)}
                      className="pr-10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="resultat_net">Résultat net comptable *</Label>
                  <div className="relative">
                    <Input
                      id="resultat_net"
                      type="number"
                      value={formData.resultat_net}
                      onChange={(e) => updateField("resultat_net", parseFloat(e.target.value) || 0)}
                      className="pr-10"
                      data-testid="resultat-net-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Ce montant doit correspondre au résultat net de votre liasse fiscale
                  </p>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${formData.resultat_net >= 0 ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'} border`}>
                <p className={`text-center text-lg font-bold ${formData.resultat_net >= 0 ? 'text-green-700' : 'text-orange-700'}`}>
                  Résultat : {formatCurrency(formData.resultat_net)}
                </p>
                <p className="text-center text-sm text-gray-500">
                  {formData.resultat_net >= 0 ? "Bénéfice" : "Perte"}
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Affectation */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Affectation du résultat</h3>
                <p className="text-gray-500">Répartissez le résultat de {formatCurrency(formData.resultat_net)}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                <div className="space-y-2">
                  <Label htmlFor="reserve_legale">Réserve légale</Label>
                  <div className="relative">
                    <Input
                      id="reserve_legale"
                      type="number"
                      value={getAffectationValue(formData.affectation.reserve_legale)}
                      onChange={(e) => updateAffectation("reserve_legale", e.target.value)}
                      className="pr-10"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  </div>
                  <p className="text-xs text-gray-500">5% du bénéfice jusqu'à 10% du capital</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="dividendes">Dividendes</Label>
                  <div className="relative">
                    <Input
                      id="dividendes"
                      type="number"
                      value={getAffectationValue(formData.affectation.dividendes)}
                      onChange={(e) => updateAffectation("dividendes", e.target.value)}
                      className="pr-10"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="report_nouveau">Report à nouveau</Label>
                  <div className="relative">
                    <Input
                      id="report_nouveau"
                      type="number"
                      value={formData.resultat_net - (parseFloat(formData.affectation.reserve_legale) || 0) - (parseFloat(formData.affectation.dividendes) || 0)}
                      readOnly
                      className="pr-10 bg-gray-100 cursor-not-allowed"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  </div>
                  <p className="text-xs text-gray-500">Calculé automatiquement</p>
                </div>
              </div>
              
              {/* Validation */}
              <div className={`p-4 rounded-lg border ${affectationValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total affectation :</span>
                  <span className={`font-bold ${affectationValid ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(totalAffectation)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Résultat à affecter :</span>
                  <span>{formatCurrency(formData.resultat_net)}</span>
                </div>
                {!affectationValid && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    L'affectation doit correspondre exactement au résultat
                  </p>
                )}
              </div>
              
              {/* Quick button to put all in report */}
              <div className="text-center">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const reserveLegale = formData.resultat_net > 0 
                      ? Math.min(formData.resultat_net * 0.05, (wizardData?.company?.capital_social || 0) * 0.1)
                      : 0;
                    updateAffectation("reserve_legale", reserveLegale);
                    updateAffectation("dividendes", 0);
                    updateAffectation("report_nouveau", formData.resultat_net - reserveLegale);
                  }}
                  className="gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Tout en report à nouveau
                </Button>
              </div>

              {/* Résolutions supplémentaires (optionnel) */}
              <div className="border-t pt-6 mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      Résolutions supplémentaires (optionnel)
                    </h4>
                    <p className="text-xs text-gray-500">
                      Ratification d'actes, quitus, conventions réglementées, etc.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowResolutionsSupp(!showResolutionsSupp)}
                    data-testid="toggle-resolutions-supp"
                  >
                    {showResolutionsSupp ? "Masquer" : "Ajouter"}
                  </Button>
                </div>

                {showResolutionsSupp && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                        Modèles prêts à l'emploi
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {AGO_SUPP_TEMPLATES.map((tpl, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => {
                              const newRes = [
                                ...(formData.resolutions_supplementaires || []),
                                { titre: tpl.titre, contenu: tpl.contenu }
                              ];
                              updateField("resolutions_supplementaires", newRes);
                            }}
                            className="text-left p-2.5 border rounded-lg bg-white hover:bg-blue-50 hover:border-blue-300 transition"
                            data-testid={`ago-supp-template-${i}`}
                          >
                            <p className="text-xs font-semibold text-foreground">{tpl.label}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{tpl.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {(formData.resolutions_supplementaires || []).map((res, idx) => (
                      <div key={idx} className="p-3 bg-white border rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <Input
                            value={res.titre}
                            onChange={(e) => {
                              const newRes = [...formData.resolutions_supplementaires];
                              newRes[idx] = { ...newRes[idx], titre: e.target.value };
                              updateField("resolutions_supplementaires", newRes);
                            }}
                            placeholder="Titre de la résolution"
                            className="text-sm font-semibold"
                            data-testid={`ago-supp-titre-${idx}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newRes = formData.resolutions_supplementaires.filter((_, i) => i !== idx);
                              updateField("resolutions_supplementaires", newRes);
                            }}
                            className="text-red-600 ml-2 flex-shrink-0"
                            data-testid={`ago-supp-remove-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <Textarea
                          value={res.contenu}
                          onChange={(e) => {
                            const newRes = [...formData.resolutions_supplementaires];
                            newRes[idx] = { ...newRes[idx], contenu: e.target.value };
                            updateField("resolutions_supplementaires", newRes);
                          }}
                          placeholder="Contenu de la résolution..."
                          rows={5}
                          className="text-sm"
                          data-testid={`ago-supp-contenu-${idx}`}
                        />
                        <p className="text-[10px] text-muted-foreground italic">
                          Remplace les [crochets] par tes valeurs (dates, montants, noms…).
                        </p>
                      </div>
                    ))}

                    {(formData.resolutions_supplementaires || []).length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-3 italic">
                        Aucune résolution supplémentaire. Clique sur un modèle ci-dessus pour l'ajouter.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Preview */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Aperçu du PV d'AGO</h3>
                <p className="text-gray-500">Vérifiez le document avant génération finale</p>
              </div>
              
              {previewLoading ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
                  <p className="text-gray-500">Génération de l'aperçu...</p>
                </div>
              ) : previewUrl ? (
                <div className="border rounded-lg overflow-hidden" style={{ height: "500px" }}>
                  <iframe
                    src={previewUrl}
                    className="w-full h-full"
                    title="Aperçu PV AGO"
                  />
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Impossible de charger l'aperçu</p>
                  <Button variant="outline" onClick={generatePreview} className="mt-4">
                    Réessayer
                  </Button>
                </div>
              )}
              
              <div className="flex items-center justify-center gap-4 pt-4 border-t">
                <Button variant="outline" onClick={generatePreview} disabled={previewLoading}>
                  <Eye className="w-4 h-4 mr-2" />
                  Rafraîchir l'aperçu
                </Button>
                
                <Button 
                  onClick={generateFinal} 
                  disabled={generating || !affectationValid}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 gap-2"
                  data-testid="generate-final-btn"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Générer et enregistrer
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          disabled={currentStep === 0}
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Précédent
        </Button>
        
        {currentStep < WIZARD_STEPS.length - 1 && (
          <Button 
            onClick={nextStep}
            disabled={(currentStep === 1 && convocationError) || (currentStep === 4 && !affectationValid)}
            className="gap-2"
            data-testid="next-step-btn"
          >
            Suivant
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
